
import { Component, Vue } from 'vue-property-decorator';
import CrossbreedingSimulator from './components/CrossbreedingSimulator.vue';
import LogoSelector from './components/LogoSelector.vue';
import InfoButtons from './components/InfoButtons.vue';
import { timeMsToTimeString } from './lib/time-utils';

@Component({
  components: {
    LogoSelector,
    InfoButtons,
    CrossbreedingSimulator
  }
})
export default class App extends Vue {
  selectedPlantTypeName: string | null = null;
  estimatedTime: number | null = null;
  totalTime: string | null = null;

  private mainBarResizeObserver: ResizeObserver | null = null;
  private mainPadRaf: number | null = null;

  get calcEstimatedTime() {
    if (this.estimatedTime) {
      return timeMsToTimeString(this.estimatedTime);
    }
    return null;
  }

  handlePlantTypeChange(name: string) {
    this.selectedPlantTypeName = name;
  }

  handleEstimatedTimeUpdated(value: number | null) {
    this.estimatedTime = value;
  }

  handleTotalTimeUpdated(value: string | null) {
    this.totalTime = value;
  }

  /**
   * Vuetify sets v-main paddingTop from $vuetify.application. The fixed hub shell lives outside #app (network-shell.js);
   * this measures only the breeder toolbar row and sets .v-main__wrap padding-top so content clears it.
   */
  syncNetworkMainPadding(): void {
    const mainRef = this.$refs.mainShell as Vue | undefined;
    const mainEl = mainRef?.$el as HTMLElement | undefined;
    const bar = this.$el?.querySelector?.('.app-studio__bar') as HTMLElement | undefined;
    if (!mainEl || !bar) return;

    const gapPx = 28;
    const pad = Math.max(120, Math.ceil(bar.getBoundingClientRect().height) + gapPx);

    mainEl.style.setProperty('padding-top', '0', 'important');
    mainEl.style.setProperty('padding-left', '0', 'important');
    mainEl.style.setProperty('padding-right', '0', 'important');

    const wrap = mainEl.querySelector('.v-main__wrap') as HTMLElement | undefined;
    if (wrap) {
      wrap.style.setProperty('padding-top', `${pad}px`, 'important');
    }
  }

  private queueMainPadding(): void {
    if (this.mainPadRaf != null) cancelAnimationFrame(this.mainPadRaf);
    this.mainPadRaf = requestAnimationFrame(() => {
      this.mainPadRaf = null;
      this.syncNetworkMainPadding();
    });
  }

  private readonly onWindowResizeForMain = (): void => {
    this.queueMainPadding();
  };

  mounted(): void {
    this.$nextTick(() => {
      this.queueMainPadding();
      setTimeout(() => this.queueMainPadding(), 0);
      setTimeout(() => this.queueMainPadding(), 120);
      setTimeout(() => this.queueMainPadding(), 400);

      const bar = this.$el?.querySelector?.('.app-studio__bar') as HTMLElement | undefined;
      if (bar && typeof ResizeObserver !== 'undefined') {
        this.mainBarResizeObserver = new ResizeObserver(() => this.queueMainPadding());
        this.mainBarResizeObserver.observe(bar);
      }
      window.addEventListener('resize', this.onWindowResizeForMain);
    });
  }

  beforeDestroy(): void {
    this.mainBarResizeObserver?.disconnect();
    this.mainBarResizeObserver = null;
    window.removeEventListener('resize', this.onWindowResizeForMain);
    if (this.mainPadRaf != null) cancelAnimationFrame(this.mainPadRaf);
  }
}
